/* eslint react/sort-comp: 0 */
import React from "react";
import * as DOMUtils from "history/DOMUtils";
import { connect } from "react-redux";
import { getFinanceExternalConfigurationObject, pslsFinanceResponseHasNoQuotes } from "./typedUtils";
import { EnvironmentEnum, getThemeClassname, isRetailer } from "../../../../common-deprecated/utils";
import { clearModal, showModal } from "../../../../common-deprecated/redux/actions/ModalActions";
import { setInsurancePersonalized, updateQuoteConfig } from "../redux/actions/FinanceInsuranceActions";
import Debug from "../../../../common-deprecated/Debug";
import type { CarType } from "../../../shared-logic/types/CarType";
import type { DispatchType, ItemsType } from "../../../../common-deprecated/redux/typeHelpers";
import type { SaveStateType } from "../redux/reducers/SaveReducer";
import type { PSLSStateType } from "../redux/reducers/PSLSReducer";
import type { FinanceRateStateType } from "../redux/reducers/FinanceRateReducer";
import type { BasketType } from "../../../shared-logic/types/BasketType";
import type { ExternalConfigType } from "../../../shared-logic/types/ExternalPriceType";
import { FINANCE_CALCULATOR, INSURANCE_CALCULATOR } from "../../../../common-deprecated/constants";
import { MODAL_FINANCE_CALCULATOR } from "./modalConstants";
import type { BuildAndBuyLabelType } from "./labels";
import { getBnbOptionalLabel } from "./labels";
import type { BuildAndBuyStateType } from "../redux/store";
import type { BnbSettingsType } from "../redux/reducers/BnbSettingsReducer";
import type { T1InjectionOptionsType, T1InjectionType } from "../../../../common-deprecated/hooks/useT1ModalInjection";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import { getInjectedModuleURL, showExternalFrame } from "./settings/bnbFinanceSettingUtils";
import type { MakolabInsuranceCompleteEventType } from "../../../shared-logic/types/FinanceInsuranceRateTypes";
import type { TradeInAemExternalDataReducerType } from "../../../../common-deprecated/features/tradein/redux/reducers/TradeInAemExternalDataReducer";
import { trackCtaCalculator } from "./tracking";
import type { NavStep } from "../../shared-logic/utils/Navigation";
import { isCod } from "../../../../common-deprecated/settings/utils/commonSettingUtils";
import type { ExternalPriceStateType } from "../../../shared-logic/types/externalPriceTypes";
import { hasMakolabInsurance } from "../../../shared-logic/utils/insuranceUtils";
import { prepareCalculator, resetCalculator } from "../../../../common-deprecated/redux/actions/UtilActions";

const IDENTIFIER_PREFIX = "cc-injection";
const HIDE_MODAL_EXT_EVENT = "hide-build-amd-modal";

export type FinanceInsuranceCalculatorLinkViewType = {
    className?: string;
    onClick: (disableTracking?: boolean) => void;
    iconType: string;
    label: BuildAndBuyLabelType;
    modalShown: boolean;
    hideModal: () => void;
    modalTitle: string;
    contentHeight: number;
};

export type FinanceInsuranceCalculatorType = typeof FINANCE_CALCULATOR | typeof INSURANCE_CALCULATOR;

export type ExternalCalculatorLinkPropType = {
    type: FinanceInsuranceCalculatorType;
    className?: string;
    // The customButton property was added to re-use this complex logic in V2.
    // In V1 you can now use FinanceInsuranceCalculatorLinkWrapper to achieve the same effect.
    // This was done so the build V2 bundle no longer contains V1 views (and bootstrap modals)
    customButton: (props: FinanceInsuranceCalculatorLinkViewType) => JSX.Element;
};

export type LabelKeyTye =
    | "financeButtonCash"
    | "financeButton"
    | "insuranceButton"
    | "financeButtonEdit"
    | "insuranceButtonEdit";

export type CalculatorLinkMapType = {
    visible: boolean;
    car: CarType;
    commonSettings: CommonSettingsType;
    bnbSettings: BnbSettingsType;
    basket: BasketType;
    items: ItemsType;
    save: SaveStateType;
    psls: PSLSStateType;
    externalPrice: ExternalPriceStateType;
    quotes: FinanceRateStateType["quotes"];
    calculatorReady: boolean;
    shouldSaveFirst: boolean;
    loadPSLS: boolean;
    containerID: string;
    labelKey: LabelKeyTye;
    isLexus: boolean;
    tradeInAemExternalData: TradeInAemExternalDataReducerType;
    hasMakolabInsurance: boolean;
    step: NavStep;
    financeProductType: string;
};

type CalculatorLinkPropType = ExternalCalculatorLinkPropType & CalculatorLinkMapType & { dispatch: DispatchType };

export type CalculatorLinkStateType = {
    modalShown: boolean;
    contentHeight: number;
};

export const isPSLSDataValidOnFinance = (
    type: FinanceInsuranceCalculatorType,
    finance: FinanceRateStateType,
    bnbSettings: BnbSettingsType,
): boolean => {
    return type === FINANCE_CALCULATOR
        ? !pslsFinanceResponseHasNoQuotes(finance, bnbSettings) || finance.fetching
        : true;
};

class FinanceInsuranceCalculatorLink extends React.Component<CalculatorLinkPropType, CalculatorLinkStateType> {
    _components: T1InjectionType | null;

    _resizeEventListener: null | (() => void);

    _hideModalEventListener: null | (() => void);

    constructor(props: CalculatorLinkPropType) {
        super(props);

        const { type, commonSettings } = this.props;

        // Define the state; toggle the show/hide of the modal
        this.state = {
            modalShown: Boolean(
                (type === FINANCE_CALCULATOR && commonSettings.query.showFinanceFrame) ||
                    (type === INSURANCE_CALCULATOR && commonSettings.query.showInsuranceFrame),
            ),
            contentHeight: 400,
        };
        this._components = null;

        this._resizeEventListener = null;
        this._hideModalEventListener = null;
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount() {
        const { commonSettings, bnbSettings, hasMakolabInsurance: hasMakolabInsuranceProp } = this.props;
        if (DOMUtils.canUseDOM) {
            if (window && window.require) {
                // Add resize event listener
                this._resizeEventListener = this.checkDimensions.bind(this);
                window.addEventListener("resize", this._resizeEventListener);

                this.checkDimensions();

                let moduleURL: string;
                if (hasMakolabInsuranceProp) {
                    moduleURL = bnbSettings.insuranceModuleURL;
                } else {
                    // Add event listener for the hide modal event
                    this._hideModalEventListener = this.hideModal.bind(this);
                    document.addEventListener(HIDE_MODAL_EXT_EVENT, this._hideModalEventListener);

                    moduleURL = `${commonSettings.urlWebsiteInjection}/scripts/T1-injection-components.js`;
                }

                // Require the amd module responsible for loading the code injected frames
                window.require([moduleURL], (components) => {
                    this._components = components;
                });
            }
        }
    }

    async componentDidUpdate(prevProps: CalculatorLinkPropType, prevState: CalculatorLinkStateType) {
        const {
            car,
            basket,
            items,
            type,
            visible,
            save,
            psls,
            externalPrice,
            quotes,
            commonSettings,
            bnbSettings,
            calculatorReady,
            shouldSaveFirst,
            loadPSLS,
            containerID,
            hasMakolabInsurance: hasMakolabInsuranceProp,
            tradeInAemExternalData,
            financeProductType,
        } = this.props;

        // If it should not be visible, return
        if (!visible || !calculatorReady) return;
        // If state remains unchanged, do nothing
        if (prevState.modalShown === this.state.modalShown && prevProps.calculatorReady === calculatorReady) return;
        // Load the data when the modal is switched from hidden to shown
        // logic related to prerequistes toyota code or PSLS are handled in the FinanceInsuranceLogic -> prepareCalculatorLogic
        if (this.state.modalShown && this._components) {
            // Personalized finance quotes also get added in the method getExternalConfigurationObject
            type AdditionalConfigType = { ConfigurationCode?: string; Environment?: string };
            const configuration: ExternalConfigType & AdditionalConfigType = getFinanceExternalConfigurationObject(
                commonSettings,
                car,
                basket,
                items,
                tradeInAemExternalData,
                quotes,
                externalPrice,
                financeProductType,
            );

            // Append the code if it exists
            if (shouldSaveFirst && !save.error && save.data && save.data.ConfigurationCode !== "") {
                configuration.ConfigurationCode = save.data.ConfigurationCode;
            }

            // Append the psls data if it exists
            if (loadPSLS && psls.data) {
                configuration.PSLS = psls.data;
            }

            // Add the parameter "environment:financeCalculatorEnvironment" to the configuration object sent for the finance frame
            if (type === FINANCE_CALCULATOR) {
                if (bnbSettings.financeCalculatorEnvironment) {
                    configuration.Environment = bnbSettings.financeCalculatorEnvironment;
                } else if (commonSettings.environment === EnvironmentEnum.Preview) {
                    configuration.Environment = "test";
                }
            }

            // Quick fix; cfr LB-839 once makolab's calculator is updated to support both TotalPriceInclPromotions and TotalPrice
            // currently this is not the case hence makolab shows the monthly rate based on TotalPrice while it should be based on TotalPriceInclPromotions
            // This is only relevant for the finance calculator + temporary, hence implementation is done here rather than in the getExternalConfigurationObject method
            if (configuration.TotalPriceInclPromotions) {
                configuration.TotalPrice = configuration.TotalPriceInclPromotions;
                delete configuration.TotalPriceInclPromotions;
            }

            // Check the querystring if the new format should be used or not
            // TODO Should be removed in the future and made finance dependent!
            const toBeSentConfiguration = commonSettings.query.useExtendedFinanceConfig
                ? {
                      Configuration: configuration,
                      AdditionalData: { FixedHeaderSelector: ".or-external-frame .or-modal-header" },
                  }
                : configuration;
            // TODO Keep the following line untill implementation is completed from makolab's side, this way it's easy to see what configuration object is posted
            Debug.info("Sending configuration object: ", toBeSentConfiguration);

            try {
                const themeClassName = getThemeClassname(car.model.id);
                const options: T1InjectionOptionsType = {
                    target: `#${containerID}`,
                    configuration: toBeSentConfiguration,
                    ...(commonSettings.brand === "toyota" && themeClassName && { theme: themeClassName }),
                };
                if (hasMakolabInsuranceProp) {
                    options.isAppendedToBody = false;

                    // Add event listeners to the insurance container, this matches *roughly* with the insurance calculator logic in the usc financeInsuranceUtils.ts
                    // search for the "timContainer" in the code base to find occurrences
                    const timContainer = document.getElementById(containerID);
                    if (timContainer) {
                        timContainer.addEventListener("TIM_WIDGET_CLOSED", this.hideModal.bind(this), { once: true });
                        timContainer.addEventListener(
                            "TIM_WIDGET_OFFER_CONFIRM",
                            (event: any) => {
                                this.hideModal.bind(this);
                                const detail = event?.detail as MakolabInsuranceCompleteEventType;
                                if (detail?.quoteId) {
                                    this.props.dispatch(setInsurancePersonalized(true));
                                    this.props.dispatch(updateQuoteConfig({ insurance: detail.quoteId }, false));
                                }
                            },
                            {
                                once: true,
                            },
                        );
                    }
                } else {
                    options.url = getInjectedModuleURL(commonSettings, bnbSettings, type);
                }

                this._components.inject(options);
            } catch (ex) {
                Debug.error("error injecting component: ", containerID);
            }
        }
    }

    componentWillUnmount() {
        if (this._resizeEventListener) window.removeEventListener("resize", this._resizeEventListener);
        if (this._hideModalEventListener)
            document.removeEventListener(HIDE_MODAL_EXT_EVENT, this._hideModalEventListener);
    }

    /**
     * Method which hides the modal; also handles the event which can be dispatched from within the code injected finance/insurance calcs to close the modal.
     */
    hideModal(): void {
        if (!this.state.modalShown) return;
        document.dispatchEvent(new CustomEvent("bss-destroy-connector-event"));
        const { type, dispatch } = this.props;
        dispatch(resetCalculator(type));
        dispatch(clearModal(MODAL_FINANCE_CALCULATOR));
        this.setState({ modalShown: false });
    }

    /**
     * Method which checks the height
     * @returns {boolean}
     */
    checkDimensions() {
        // Working with hard coded values for several reasons:
        // 1. Impossible to get correct positioning values since it's animated using CSS3 animations
        // 2. Will need to render the modal first before we can request any dimensions/offset
        // 3. Need to incorporate an additional layer of complexity in the component did update for calculating the height
        const { isLexus } = this.props;
        const topOffset = window.innerWidth < 768 ? (isLexus ? 97 : 50) : isLexus ? 97 : 80;
        const bottomOffset = window.innerWidth < 768 ? (isLexus ? 50 : 0) : isLexus ? 70 : 30;
        const contentHeight = window.innerHeight - (topOffset + bottomOffset);

        if (contentHeight !== this.state.contentHeight) {
            this.setState({
                contentHeight: contentHeight ?? 0,
            });
        }
    }

    render(): JSX.Element | null {
        const {
            className,
            type,
            dispatch,
            shouldSaveFirst,
            loadPSLS,
            visible,
            labelKey,
            containerID,
            customButton,
            step,
        } = this.props;
        if (!visible) return null;
        const { contentHeight } = this.state;

        let modalTitle: BuildAndBuyLabelType;
        let icon = "";
        switch (type) {
            case INSURANCE_CALCULATOR:
                modalTitle = "insuranceModalTitle";
                icon = "insurance";
                break;
            case FINANCE_CALCULATOR:
                modalTitle = "financeModalTitle";
                icon = "fin-calculator";
                break;
        }

        const hideModalCB = (): void => this.hideModal();
        const props = {
            className,
            onClick: (disableTracking?: boolean) => {
                if (!disableTracking) {
                    trackCtaCalculator(step, type === "finance" ? "finance-calculator" : "insurance-calculator");
                }
                this.setState({ modalShown: true });
                dispatch(prepareCalculator(shouldSaveFirst, loadPSLS));
                dispatch(
                    showModal({
                        type: MODAL_FINANCE_CALCULATOR,
                        title: modalTitle,
                        containerID,
                        resetCalculator: hideModalCB,
                        calculatorType: type,
                    }),
                );
            },
            iconType: icon,
            label: labelKey,
            modalShown: this.state.modalShown,
            hideModal: hideModalCB,
            modalTitle,
            contentHeight,
        };

        // You can override the default view and its props via a "RenderProp"
        if (customButton) return customButton(props);
        Debug.error(
            "FinanceInsuranceCalculatorLink no longer has a view on it's own, you need to supply a customButton.",
        );
        return null;
    }
}

export const mapStateToProps = (
    {
        car,
        basket,
        items,
        save,
        psls,
        finance,
        commonSettings,
        utils,
        externalPrice,
        bnbSettings,
        tradeInAemExternalData,
        navigation,
    }: BuildAndBuyStateType,
    { type }: ExternalCalculatorLinkPropType,
): CalculatorLinkMapType => {
    let labelKey = "financeButton" as LabelKeyTye;

    const { quotes } = finance;
    if (type === FINANCE_CALCULATOR) {
        if (utils.financeOption === "cash" && getBnbOptionalLabel({ commonSettings, bnbSettings }, "financeButtonCash"))
            labelKey = "financeButtonCash";
        else if (quotes && quotes.finance && getBnbOptionalLabel({ commonSettings, bnbSettings }, "financeButtonEdit"))
            labelKey = "financeButtonEdit";
        else labelKey = "financeButton";
    } else if (type === INSURANCE_CALCULATOR) {
        if (quotes && quotes.insurance && getBnbOptionalLabel({ commonSettings, bnbSettings }, "insuranceButtonEdit"))
            labelKey = "insuranceButtonEdit";
        else labelKey = "insuranceButton";
    }
    return {
        car,
        basket,
        items,
        save,
        psls,
        externalPrice,
        containerID: `${IDENTIFIER_PREFIX}-${type}-amd`,
        quotes: finance.quotes,
        commonSettings,
        bnbSettings,
        calculatorReady: utils.calculatorReady,
        labelKey,
        visible:
            !isCod(commonSettings) &&
            !isRetailer(commonSettings) && // OR-8806: This is a temporary measure
            showExternalFrame(commonSettings, bnbSettings, type, car) &&
            !!getBnbOptionalLabel({ commonSettings, bnbSettings }, labelKey) &&
            isPSLSDataValidOnFinance(type, finance, bnbSettings),
        shouldSaveFirst: type === FINANCE_CALCULATOR && bnbSettings.sentFinanceStepToyotaCode,
        loadPSLS:
            (type === FINANCE_CALCULATOR && bnbSettings.addPSLSToFinanceConfigurationObject) ||
            (type === INSURANCE_CALCULATOR && bnbSettings.addPSLSToInsuranceConfigurationObject),
        isLexus: commonSettings.brand === "lexus",
        hasMakolabInsurance: type === INSURANCE_CALCULATOR && hasMakolabInsurance(bnbSettings),
        tradeInAemExternalData,
        step: navigation.step,
        financeProductType: finance.financeProductType,
    };
};

export default connect(mapStateToProps)(FinanceInsuranceCalculatorLink);
